/**
 * Team Management Page
 *
 * Allows account owners to:
 * - View list of team members
 * - Invite new technicians via email
 * - Activate/deactivate users
 * - View who created which assessments
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { canManageTeam } from '../utils/permissions';
import PermissionEditor from '../components/PermissionEditor';
import { usePermissions } from '../hooks/usePermissions';

export default function Team() {
  const { user, loading: authLoading } = useAuth();
  const { getPermissionSummary } = usePermissions();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersPermissions, setTeamMembersPermissions] = useState({});
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('technician');

  // Granular permissions for invite
  const [invitePermissions, setInvitePermissions] = useState({
    // Assessments
    can_create_assessments: true,
    can_view_all_assessments: true,
    can_view_own_assessments_only: false,
    can_edit_assessments: false,
    can_delete_assessments: false,
    can_share_assessments: true,

    // Customers
    can_view_customers: true,
    can_create_customers: true,
    can_edit_customers: false,
    can_delete_customers: false,

    // Team & Settings
    can_view_team: false,
    can_invite_users: false,
    can_manage_team: false,
    can_access_settings: false,
    can_manage_billing: false,

    // Reporting
    can_view_dashboard: true,
    can_view_analytics: false,
    can_export_reports: false,

    // Access Mode
    access_mode: 'office'
  });
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showPermissionEditor, setShowPermissionEditor] = useState(false);

  // Permission check - only owners and admins can access team management
  useEffect(() => {
    // Only check permissions after auth is fully loaded and user has role data
    if (!authLoading && user && user.role && !canManageTeam(user)) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Fetch team members and pending invites
  useEffect(() => {
    if (user?.contractor_id) {
      fetchTeamMembers();
      fetchPendingInvites();
      fetchTeamPermissions();
    }
  }, [user]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('contractor_id', user.contractor_id)
        .order('created_at', { ascending: false});

      if (fetchError) throw fetchError;
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamPermissions = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('contractor_id', user.contractor_id);

      if (fetchError) throw fetchError;

      // Convert array to object keyed by user_id for easy lookup
      const permissionsMap = {};
      (data || []).forEach(perm => {
        permissionsMap[perm.user_id] = perm;
      });
      setTeamMembersPermissions(permissionsMap);
    } catch (err) {
      console.error('Error fetching team permissions:', err);
    }
  };

  const handleInvitePermissionToggle = (permissionName) => {
    setInvitePermissions(prev => ({
      ...prev,
      [permissionName]: !prev[permissionName]
    }));
  };

  const handleInviteAccessModeChange = (mode) => {
    setInvitePermissions(prev => ({
      ...prev,
      access_mode: mode
    }));
  };

  const fetchPendingInvites = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('invite_tokens')
        .select('*')
        .eq('contractor_id', user.contractor_id)
        .is('accepted_at', null)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setPendingInvites(data || []);
    } catch (err) {
      console.error('Error fetching pending invites:', err);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError(null);

    try {
      // Generate a unique invite token
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days

      // Create invite token with role and all permissions
      const { error: inviteError } = await supabase
        .from('invite_tokens')
        .insert({
          contractor_id: user.contractor_id,
          invited_by: user.id,
          email: inviteEmail.toLowerCase(),
          full_name: inviteName,
          token: token,
          expires_at: expiresAt.toISOString(),
          role: inviteRole,
          // Store all permissions
          access_mode: invitePermissions.access_mode,
          can_create_assessments: invitePermissions.can_create_assessments,
          can_view_all_assessments: invitePermissions.can_view_all_assessments,
          can_view_own_assessments_only: invitePermissions.can_view_own_assessments_only,
          can_edit_assessments: invitePermissions.can_edit_assessments,
          can_delete_assessments: invitePermissions.can_delete_assessments,
          can_share_assessments: invitePermissions.can_share_assessments,
          can_view_customers: invitePermissions.can_view_customers,
          can_create_customers: invitePermissions.can_create_customers,
          can_edit_customers: invitePermissions.can_edit_customers,
          can_delete_customers: invitePermissions.can_delete_customers,
          can_view_team: invitePermissions.can_view_team,
          can_invite_users: invitePermissions.can_invite_users,
          can_manage_team: invitePermissions.can_manage_team,
          can_access_settings: invitePermissions.can_access_settings,
          can_manage_billing: invitePermissions.can_manage_billing,
          can_view_dashboard: invitePermissions.can_view_dashboard,
          can_view_analytics: invitePermissions.can_view_analytics,
          can_export_reports: invitePermissions.can_export_reports
        });

      if (inviteError) throw inviteError;

      // Send invite email via Netlify Function
      const emailResponse = await fetch('/.netlify/functions/send-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inviteToken: token,
          inviteeEmail: inviteEmail.toLowerCase(),
          inviteeName: inviteName,
          inviterName: user.full_name,
          companyName: user.contractors?.company_name
        })
      });

      if (!emailResponse.ok) {
        const emailError = await emailResponse.json();
        throw new Error(emailError.message || 'Failed to send invite email');
      }

      setInviteSuccess(true);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('technician');

      // Reset permissions to defaults
      setInvitePermissions({
        can_create_assessments: true,
        can_view_all_assessments: true,
        can_view_own_assessments_only: false,
        can_edit_assessments: false,
        can_delete_assessments: false,
        can_share_assessments: true,
        can_view_customers: true,
        can_create_customers: true,
        can_edit_customers: false,
        can_delete_customers: false,
        can_view_team: false,
        can_invite_users: false,
        can_manage_team: false,
        can_access_settings: false,
        can_manage_billing: false,
        can_view_dashboard: true,
        can_view_analytics: false,
        can_export_reports: false,
        access_mode: 'office'
      });

      setTimeout(() => {
        setShowInviteForm(false);
        setInviteSuccess(false);
        fetchPendingInvites(); // Refresh pending invites list
      }, 3000);

    } catch (err) {
      console.error('Error sending invite:', err);
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      setError(null);

      // Resend email via Netlify Function
      const emailResponse = await fetch('/.netlify/functions/send-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inviteToken: invite.token,
          inviteeEmail: invite.email,
          inviteeName: invite.email.split('@')[0], // Use email prefix as name fallback
          inviterName: user.full_name,
          companyName: user.contractors?.company_name
        })
      });

      if (!emailResponse.ok) {
        const emailError = await emailResponse.json();
        throw new Error(emailError.message || 'Failed to resend invite email');
      }

      alert(`Invitation resent to ${invite.email}`);
    } catch (err) {
      console.error('Error resending invite:', err);
      setError(err.message);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      setError(null);
      const { error: deleteError } = await supabase
        .from('invite_tokens')
        .delete()
        .eq('id', inviteId);

      if (deleteError) throw deleteError;

      // Refresh pending invites list
      fetchPendingInvites();
    } catch (err) {
      console.error('Error revoking invite:', err);
      setError(err.message);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({ is_active: !currentStatus })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Refresh team members list
      fetchTeamMembers();
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.message);
    }
  };

  const handleEditPermissions = (member) => {
    setEditingMember(member);
    setShowPermissionEditor(true);
  };

  const handlePermissionsSaved = async () => {
    // Refresh permissions
    await fetchTeamPermissions();
  };

  const getPermissionBadges = (userId, userRole) => {
    // Owners/admins always have full access
    if (userRole === 'owner' || userRole === 'admin') {
      return (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
          <span style={{
            padding: '4px 8px',
            background: '#dcfce7',
            color: '#166534',
            fontSize: '11px',
            fontWeight: '600',
            borderRadius: '4px',
            border: '1px solid #86efac'
          }}>
            📋 Assessments: Full
          </span>
          <span style={{
            padding: '4px 8px',
            background: '#dcfce7',
            color: '#166534',
            fontSize: '11px',
            fontWeight: '600',
            borderRadius: '4px',
            border: '1px solid #86efac'
          }}>
            👥 Customers: Full
          </span>
          <span style={{
            padding: '4px 8px',
            background: '#dcfce7',
            color: '#166534',
            fontSize: '11px',
            fontWeight: '600',
            borderRadius: '4px',
            border: '1px solid #86efac'
          }}>
            🔧 Settings: Full
          </span>
        </div>
      );
    }

    const perms = teamMembersPermissions[userId];
    if (!perms) return null;

    const badges = [];

    // Assessments badge
    let assessmentsLabel = 'No Access';
    let assessmentsColor = '#fee2e2';
    let assessmentsBorder = '#fca5a5';
    let assessmentsTextColor = '#991b1b';

    if (perms.can_view_own_assessments_only) {
      assessmentsLabel = 'Own Only';
      assessmentsColor = '#fef3c7';
      assessmentsBorder = '#fcd34d';
      assessmentsTextColor = '#92400e';
    } else if (perms.can_edit_assessments) {
      assessmentsLabel = 'Full';
      assessmentsColor = '#dcfce7';
      assessmentsBorder = '#86efac';
      assessmentsTextColor = '#166534';
    } else if (perms.can_view_all_assessments) {
      assessmentsLabel = 'View All';
      assessmentsColor = '#dbeafe';
      assessmentsBorder = '#93c5fd';
      assessmentsTextColor = '#1e3a8a';
    } else if (perms.can_create_assessments) {
      assessmentsLabel = 'Create';
      assessmentsColor = '#e0e7ff';
      assessmentsBorder = '#a5b4fc';
      assessmentsTextColor = '#3730a3';
    }

    badges.push(
      <span key="assessments" style={{
        padding: '4px 8px',
        background: assessmentsColor,
        color: assessmentsTextColor,
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px',
        border: `1px solid ${assessmentsBorder}`
      }}>
        📋 Assessments: {assessmentsLabel}
      </span>
    );

    // Customers badge
    let customersLabel = 'No Access';
    let customersColor = '#fee2e2';
    let customersBorder = '#fca5a5';
    let customersTextColor = '#991b1b';

    if (perms.can_edit_customers || perms.can_delete_customers) {
      customersLabel = 'Full';
      customersColor = '#dcfce7';
      customersBorder = '#86efac';
      customersTextColor = '#166534';
    } else if (perms.can_view_customers && perms.can_create_customers) {
      customersLabel = 'View & Create';
      customersColor = '#dbeafe';
      customersBorder = '#93c5fd';
      customersTextColor = '#1e3a8a';
    } else if (perms.can_view_customers) {
      customersLabel = 'View Only';
      customersColor = '#fef3c7';
      customersBorder = '#fcd34d';
      customersTextColor = '#92400e';
    }

    badges.push(
      <span key="customers" style={{
        padding: '4px 8px',
        background: customersColor,
        color: customersTextColor,
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px',
        border: `1px solid ${customersBorder}`
      }}>
        👥 Customers: {customersLabel}
      </span>
    );

    // Settings badge
    if (perms.can_access_settings || perms.can_manage_billing) {
      badges.push(
        <span key="settings" style={{
          padding: '4px 8px',
          background: '#dcfce7',
          color: '#166534',
          fontSize: '11px',
          fontWeight: '600',
          borderRadius: '4px',
          border: '1px solid #86efac'
        }}>
          🔧 Settings: Full
        </span>
      );
    } else {
      badges.push(
        <span key="settings" style={{
          padding: '4px 8px',
          background: '#fee2e2',
          color: '#991b1b',
          fontSize: '11px',
          fontWeight: '600',
          borderRadius: '4px',
          border: '1px solid #fca5a5'
        }}>
          🚫 Settings: No Access
        </span>
      );
    }

    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
        {badges}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading team members...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h1 style={{ fontSize: '28px', marginBottom: '5px' }}>Team Management</h1>
            <p style={{ color: '#7f8c8d', margin: 0 }}>
              Manage your technicians and team members
            </p>
          </div>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="btn btn-primary"
          >
            {showInviteForm ? '✕ Cancel' : '✉️ Invite Team Member'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Invite Form */}
        {showInviteForm && (
          <div className="card" style={{ marginBottom: '30px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #bae6fd' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '20px', color: '#0369a1' }}>
              Invite New Team Member
            </h3>

            {inviteSuccess ? (
              <div style={{
                padding: '20px',
                background: '#dcfce7',
                border: '2px solid #22c55e',
                borderRadius: '8px',
                color: '#14532d',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
                <div style={{ fontSize: '18px', fontWeight: '600' }}>Invite Sent!</div>
                <div style={{ fontSize: '14px', marginTop: '5px' }}>
                  {inviteEmail} will receive an email with instructions to join your team.
                </div>
              </div>
            ) : (
              <form onSubmit={handleInvite}>
                {/* Basic Info */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0c4a6e' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Smith"
                    required
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0c4a6e' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0c4a6e' }}>
                    Role
                  </label>
                  <select
                    className="input"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="technician">Technician</option>
                    <option value="owner">Owner</option>
                  </select>
                  <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Select base role for this team member
                  </span>
                </div>

                {/* Access Mode Selection */}
                <div style={{ marginBottom: '25px', padding: '20px', background: 'white', borderRadius: '8px', border: '2px solid #bae6fd' }}>
                  <h4 style={{ fontSize: '16px', marginBottom: '15px', color: '#0c4a6e', fontWeight: '600' }}>
                    🎨 Interface Mode
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => handleInviteAccessModeChange('office')}
                      className="btn"
                      style={{
                        background: invitePermissions.access_mode === 'office' ? '#0284c7' : 'white',
                        color: invitePermissions.access_mode === 'office' ? 'white' : '#2c3e50',
                        border: `2px solid ${invitePermissions.access_mode === 'office' ? '#0284c7' : '#bae6fd'}`,
                        padding: '12px',
                        fontSize: '14px'
                      }}
                    >
                      💼 Office Mode
                      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>
                        Full desktop
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInviteAccessModeChange('field')}
                      className="btn"
                      style={{
                        background: invitePermissions.access_mode === 'field' ? '#0284c7' : 'white',
                        color: invitePermissions.access_mode === 'field' ? 'white' : '#2c3e50',
                        border: `2px solid ${invitePermissions.access_mode === 'field' ? '#0284c7' : '#bae6fd'}`,
                        padding: '12px',
                        fontSize: '14px'
                      }}
                    >
                      📱 Field Mode
                      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>
                        Simplified mobile
                      </div>
                    </button>
                  </div>
                </div>

                {/* Permission Categories */}
                <div style={{ display: 'grid', gap: '15px', marginBottom: '25px' }}>
                  {/* Assessments */}
                  <InvitePermissionCategory
                    title="📋 Assessments"
                    permissions={[
                      { key: 'can_create_assessments', label: 'Create new assessments' },
                      { key: 'can_view_all_assessments', label: 'View all team assessments' },
                      { key: 'can_view_own_assessments_only', label: 'View only own assessments' },
                      { key: 'can_edit_assessments', label: 'Edit any assessment' },
                      { key: 'can_delete_assessments', label: 'Delete assessments' },
                      { key: 'can_share_assessments', label: 'Share assessments with customers' }
                    ]}
                    permissionState={invitePermissions}
                    onToggle={handleInvitePermissionToggle}
                  />

                  {/* Customers */}
                  <InvitePermissionCategory
                    title="👥 Customers"
                    permissions={[
                      { key: 'can_view_customers', label: 'View customer list' },
                      { key: 'can_create_customers', label: 'Create new customers' },
                      { key: 'can_edit_customers', label: 'Edit customer information' },
                      { key: 'can_delete_customers', label: 'Delete customers' }
                    ]}
                    permissionState={invitePermissions}
                    onToggle={handleInvitePermissionToggle}
                  />

                  {/* Team & Settings */}
                  <InvitePermissionCategory
                    title="🔧 Team & Settings"
                    permissions={[
                      { key: 'can_view_team', label: 'View team page' },
                      { key: 'can_invite_users', label: 'Invite new users' },
                      { key: 'can_manage_team', label: 'Manage team members' },
                      { key: 'can_access_settings', label: 'Access settings page' },
                      { key: 'can_manage_billing', label: 'Manage billing & subscription' }
                    ]}
                    permissionState={invitePermissions}
                    onToggle={handleInvitePermissionToggle}
                  />

                  {/* Reporting & Analytics */}
                  <InvitePermissionCategory
                    title="📊 Reporting & Analytics"
                    permissions={[
                      { key: 'can_view_dashboard', label: 'View dashboard' },
                      { key: 'can_view_analytics', label: 'View analytics page' },
                      { key: 'can_export_reports', label: 'Export reports (PDF, CSV)' }
                    ]}
                    permissionState={invitePermissions}
                    onToggle={handleInvitePermissionToggle}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={inviting}
                  style={{ width: '100%' }}
                >
                  {inviting ? 'Sending Invite...' : 'Send Invitation'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <div className="card" style={{ marginBottom: '30px', background: '#fef3c7', border: '2px solid #fbbf24' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#92400e' }}>
              ⏳ Pending Invitations ({pendingInvites.length})
            </h2>

            <div style={{ display: 'grid', gap: '15px' }}>
              {pendingInvites.map((invite) => {
                const isExpired = new Date(invite.expires_at) < new Date();
                const daysLeft = Math.ceil((new Date(invite.expires_at) - new Date()) / (1000 * 60 * 60 * 24));

                return (
                  <div
                    key={invite.id}
                    className="mobile-stack"
                    style={{
                      padding: '20px',
                      background: isExpired ? '#fee2e2' : 'white',
                      border: `2px solid ${isExpired ? '#ef4444' : '#d4d4d8'}`,
                      borderRadius: '8px',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: '20px',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                          {invite.email}
                        </div>
                        {isExpired && (
                          <span style={{
                            padding: '4px 10px',
                            background: '#ef4444',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            borderRadius: '12px'
                          }}>
                            EXPIRED
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                        Invited {new Date(invite.created_at).toLocaleDateString()} at {new Date(invite.created_at).toLocaleTimeString()}
                      </div>

                      <div style={{ fontSize: '14px', color: isExpired ? '#dc2626' : '#64748b' }}>
                        {isExpired
                          ? `Expired ${new Date(invite.expires_at).toLocaleDateString()}`
                          : `Expires in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} (${new Date(invite.expires_at).toLocaleDateString()})`
                        }
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                      <button
                        onClick={() => handleResendInvite(invite)}
                        className="btn btn-secondary"
                        style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                      >
                        📧 Resend
                      </button>
                      <button
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="btn"
                        style={{
                          fontSize: '14px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '2px solid #ef4444'
                        }}
                      >
                        ✕ Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Team Members List */}
        <div className="card">
          <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>
            Team Members ({teamMembers.length})
          </h2>

          {teamMembers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>👥</div>
              <div style={{ fontSize: '16px' }}>No team members yet</div>
              <div style={{ fontSize: '14px', marginTop: '5px' }}>
                Click "Invite Technician" to add your first team member
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '15px' }}>
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="mobile-stack"
                  style={{
                    padding: '20px',
                    background: member.is_active ? '#f8fafc' : '#fef3c7',
                    border: `2px solid ${member.is_active ? '#e0e6ed' : '#fbbf24'}`,
                    borderRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '20px',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                        {member.full_name}
                      </div>
                      {member.id === user.id && (
                        <span style={{
                          padding: '4px 10px',
                          background: '#667eea',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600',
                          borderRadius: '12px'
                        }}>
                          You
                        </span>
                      )}
                      <span style={{
                        padding: '4px 10px',
                        background: member.role === 'owner' ? '#10b981' : '#64748b',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '12px',
                        textTransform: 'uppercase'
                      }}>
                        {member.role}
                      </span>
                      {!member.is_active && (
                        <span style={{
                          padding: '4px 10px',
                          background: '#f59e0b',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600',
                          borderRadius: '12px'
                        }}>
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                      {member.email}
                    </div>
                    {member.phone && (
                      <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                        📞 {member.phone}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                      Joined {new Date(member.created_at).toLocaleDateString()}
                    </div>

                    {/* Permission Badges */}
                    {getPermissionBadges(member.id, member.role)}
                  </div>

                  {/* Actions */}
                  {member.id !== user.id && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <button
                        onClick={() => handleEditPermissions(member)}
                        className="btn btn-primary"
                        style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                      >
                        ⚙️ Edit Permissions
                      </button>
                      <button
                        onClick={() => toggleUserStatus(member.id, member.is_active)}
                        className="btn btn-secondary"
                        style={{ fontSize: '14px' }}
                      >
                        {member.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="card" style={{ marginTop: '30px', background: '#fffbeb', border: '2px solid #fbbf24' }}>
          <div style={{ fontSize: '14px', color: '#92400e', lineHeight: '1.6' }}>
            <strong>💡 Team Management Tips:</strong>
            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <li>Only account owners can invite and manage team members</li>
              <li>Use "Edit Permissions" to customize what each team member can do</li>
              <li>Deactivated users cannot log in or create assessments</li>
              <li>Assessment visibility is controlled in Settings</li>
              <li>Invite links expire after 7 days</li>
            </ul>
          </div>
        </div>

        {/* Permission Editor Modal */}
        <PermissionEditor
          member={editingMember}
          isOpen={showPermissionEditor}
          onClose={() => {
            setShowPermissionEditor(false);
            setEditingMember(null);
          }}
          onSave={handlePermissionsSaved}
        />
      </div>
    </div>
  );
}

// Helper component for invite permission categories
function InvitePermissionCategory({ title, permissions, permissionState, onToggle }) {
  return (
    <div style={{
      padding: '15px',
      background: 'white',
      borderRadius: '8px',
      border: '2px solid #bae6fd'
    }}>
      <h4 style={{ fontSize: '15px', marginBottom: '12px', color: '#0c4a6e', fontWeight: '600' }}>
        {title}
      </h4>
      <div style={{ display: 'grid', gap: '8px' }}>
        {permissions.map(({ key, label }) => (
          <label
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '8px',
              background: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #e0f2fe',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#0284c7';
              e.currentTarget.style.background = '#e0f2fe';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e0f2fe';
              e.currentTarget.style.background = '#f0f9ff';
            }}
          >
            <input
              type="checkbox"
              checked={permissionState[key]}
              onChange={() => onToggle(key)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#0284c7'
              }}
            />
            <span style={{ fontSize: '13px', color: '#0c4a6e' }}>{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
